// src/components/Team.tsx
// The "Tim" surface. Since the navigation rework (2026-07-27) this is NOT a
// standalone page any more: it renders as the centre column of the Chat shell
// (route /team points at <Chat/>, which mounts <TeamPanel/> here). It therefore
// owns NO chrome of its own - no logo strip, no "Kembali ke chat" (the top bar's
// back chevron is the way out) and no scroll container (the shell provides one).
// It still guards itself Chat-style: any 401 from the API clears localStorage
// inside teamService and we redirect to /login here.
//
// Styling follows the in-app Obsidian idiom on purpose: themeColors[theme] read
// through the `theme` PROP (the shell owns the toggle, so an own useTheme()
// instance would desync) + the global .pa-* classes.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Theme, themeColors } from '../theme/colors';
import { parseServerDate } from './Sidebar';
import { takeTeamJoinError } from './chat/teamWelcome';
import {
  getTeamMe,
  createTeam,
  deleteTeam,
  createInvite,
  revokeInvite,
  resendInvite,
  updateMemberLimit,
  removeMember,
  leaveTeam,
  getPacks,
  createTopup,
  purchaseTeamPlan,
  purchaseTeamSeats,
  getTeamAnalytics,
  TeamAnalytics,
  getLedger,
  listProjects,
  createProject,
  formatCredits,
  formatRupiah,
  TeamError,
  TeamUnauthorizedError,
  TeamMe,
  CreditPack,
  TeamPlanOffer,
  LedgerEntry,
  ProjectRow,
  ProjectVisibility,
} from './chat/teamService';
import TeamPeople from './TeamPeople';
import TeamPlaybooks from './TeamPlaybooks';
import TeamPrograms from './TeamPrograms';
import TeamChampion from './TeamChampion';
import TeamGuide from './TeamGuide';

const MAX_TEAM_NAME = 60;
const MAX_PROJECT_NAME = 80;

// Pricing v3: the Team plan is one monthly price, bought per cycle. The real
// number comes from GET /teams/packs (`plan.price_idr`) so the button and the
// invoice can never disagree; this constant is only the fallback for the split
// second before that call lands, or a backend that has not shipped the block.
const TEAM_PLAN_PRICE_FALLBACK = 'Rp399.000';

// "26 Jul 2026" from a naive-UTC server timestamp.
const shortDate = (iso?: string): string => {
  if (!iso) return '';
  const d = parseServerDate(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ADOPTION_STATE_LABEL: Record<string, string> = {
  joined: 'Bergabung',
  trying: 'Mencoba',
  exploring: 'Mengeksplorasi',
  routine: 'Rutin',
  embedded: 'Melekat',
  declining: 'Berisiko turun',
};

const ADOPTION_FILTERS = [
  'joined', 'trying', 'exploring', 'routine', 'embedded', 'declining',
] as const;

function downloadAdoptionCsv(analytics: TeamAnalytics) {
  const cols = [
    'email', 'role', 'state', 'credits_month', 'turns_month',
    'active_days_28d', 'last_active_at',
  ];
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const lines = [cols.join(',')];
  for (const m of analytics.members) {
    lines.push([
      m.email,
      m.role,
      m.adoption_state || '',
      String(m.credits_month),
      String(m.turns_month),
      String(m.active_days_28d ?? m.active_days_month),
      m.last_active_at || '',
    ].map(escape).join(','));
  }
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = 'adopsi-tim.csv';
  a.click();
  URL.revokeObjectURL(href);
}

export interface TeamPanelProps {
  // Owned by the shell so the sidebar toggle re-themes this panel too.
  theme: Theme;
  // Anything that changes team membership, projects or the pool: the shell
  // refetches credit-info + its project/shared lists so the spine agrees with
  // the panel without a reload.
  onDataChanged?: () => void;
}

const TeamPanel: React.FC<TeamPanelProps> = ({ theme, onDataChanged }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const c = themeColors[theme];
  const isCommandCenter = location.pathname.startsWith('/team/adoption');
  const isPeopleSurface = location.pathname.startsWith('/team/people');
  const isPlaybooksSurface = location.pathname.startsWith('/team/playbooks');
  const isProgramsSurface = location.pathname.startsWith('/team/programs');
  const isChampionSurface = location.pathname.startsWith('/team/champion');
  const stateFilter = searchParams.get('state') || '';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [me, setMe] = useState<TeamMe | null>(null);

  // No-team state: create form
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  // Why this person is looking at a create-team form despite clicking an
  // invite: the 409 'team_full' race, relayed once from the login screen.
  const [joinError, setJoinError] = useState('');

  // Invites
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteNotice, setInviteNotice] = useState('');
  const [resending, setResending] = useState(false);

  // Per-member monthly limit editor (drafts keyed by user_id)
  const [limitDrafts, setLimitDrafts] = useState<Record<number, string>>({});
  const [savingLimitFor, setSavingLimitFor] = useState<number | null>(null);
  const [memberError, setMemberError] = useState('');
  // Which member's limit was just saved. Same inline confirmation the project
  // instructions editor uses ("Instruksi tersimpan."): the row's own number
  // barely changes, so without a word nothing tells the owner it worked.
  const [savedLimitFor, setSavedLimitFor] = useState<number | null>(null);

  // Projects (Phase 3). The list is best-effort: a backend without the route
  // yet answers 404, and we then render the create form over an empty list
  // rather than surfacing an error the user cannot act on.
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [analytics, setAnalytics] = useState<TeamAnalytics | null>(null);
  const [analyticsStatus, setAnalyticsStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [projectName, setProjectName] = useState('');
  const [projectVisibility, setProjectVisibility] = useState<ProjectVisibility>('team');
  const [creatingProject, setCreatingProject] = useState(false);
  const [projectError, setProjectError] = useState('');

  // Catalog (GET /teams/packs): the plan on offer + the top-up packs.
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [planOffer, setPlanOffer] = useState<TeamPlanOffer | null>(null);
  const [topupPending, setTopupPending] = useState<string | null>(null);
  const [topupClosed, setTopupClosed] = useState(false);
  const [topupError, setTopupError] = useState('');

  // Team plan (pricing v3). Same money flow as the top-up, same freeze flag, so
  // it reuses the calm "belum dibuka" notice rather than raising an error.
  const [planPending, setPlanPending] = useState(false);
  const [planClosed, setPlanClosed] = useState(false);
  const [planError, setPlanError] = useState('');

  // Payment returns land back here: ?topup=, ?plan= and ?seats=success. One
  // state, because only the wording differs.
  const [paymentSuccess, setPaymentSuccess] = useState<'plan' | 'topup' | 'seats' | null>(
    searchParams.get('plan') === 'success'
      ? 'plan'
      : searchParams.get('topup') === 'success'
        ? 'topup'
        : searchParams.get('seats') === 'success'
          ? 'seats'
          : null
  );

  // Kursi tambahan (flexible seats). Same tri-state idiom as the plan.
  const [seatQty, setSeatQty] = useState(1);
  const [seatPending, setSeatPending] = useState(false);
  const [seatClosed, setSeatClosed] = useState(false);
  const [seatError, setSeatError] = useState('');

  // Ledger
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [ledgerHasMore, setLedgerHasMore] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState('');

  // Leave / delete
  const [busyAction, setBusyAction] = useState(false);
  const [actionError, setActionError] = useState('');

  // Inline confirms. There is no window.confirm anywhere in the app any more:
  // the triggering button itself flips into "Yakin …?" + "Batal".
  const [confirmRemoveMember, setConfirmRemoveMember] = useState<number | null>(null);
  const [confirmMembership, setConfirmMembership] = useState<'leave' | 'delete' | null>(null);

  // Which sub-nav section the reader is standing in. Set on click and kept
  // honest by a scroll listener, so the strip stops being five identical pills.
  const [activeSection, setActiveSection] = useState<string>('team-anggota');

  // Maps any thrown error to a renderable Indonesian string. A 401 redirects
  // instead and returns '' so callers skip their own error state.
  const describe = useCallback(
    (err: unknown): string => {
      if (err instanceof TeamUnauthorizedError) {
        navigate('/login');
        return '';
      }
      if (err instanceof TeamError) return err.detail;
      return 'Terjadi kesalahan jaringan. Coba lagi.';
    },
    [navigate]
  );

  const loadTeam = useCallback(async (): Promise<TeamMe | null> => {
    try {
      const data = await getTeamMe();
      setMe(data);
      setLoadError('');
      return data;
    } catch (err) {
      const msg = describe(err);
      if (msg) setLoadError(msg);
      return null;
    }
  }, [describe]);

  const loadLedger = useCallback(
    async (beforeId: number | null) => {
      setLedgerLoading(true);
      try {
        const page = await getLedger(50, beforeId);
        setLedger((prev) => (beforeId == null ? page.entries : [...prev, ...page.entries]));
        setLedgerHasMore(page.has_more);
        setLedgerError('');
      } catch (err) {
        const msg = describe(err);
        if (msg) setLedgerError(msg);
      } finally {
        setLedgerLoading(false);
      }
    },
    [describe]
  );

  // Adopsi (owner only). Best-effort like projects: any failure leaves it
  // null and the section simply does not render.
  const loadAnalytics = useCallback(async () => {
    setAnalyticsStatus('loading');
    try {
      setAnalytics(await getTeamAnalytics());
      setAnalyticsStatus('ready');
    } catch (err) {
      setAnalytics(null);
      setAnalyticsStatus('error');
      describe(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [describe]);

  // Never blocks the page: 404 (route not deployed) or any other failure just
  // leaves the list empty. A 401 still redirects, via describe().
  const loadProjects = useCallback(async () => {
    try {
      setProjects(await listProjects());
    } catch (err) {
      setProjects([]);
      describe(err);
    }
  }, [describe]);

  const loadPacks = useCallback(async () => {
    try {
      const catalog = await getPacks();
      setPacks(catalog.packs);
      setPlanOffer(catalog.plan);
    } catch (err) {
      // The catalog is decoration until someone wants to buy; a failure here
      // must never blank the page (the price copy falls back to its constant).
      describe(err);
    }
  }, [describe]);

  // Initial load. No ProtectedRoute wraps this page, so the token check is ours.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!localStorage.getItem('accessToken')) {
        navigate('/login');
        return;
      }
      // Read-and-clear: shown once, on the first team screen after the failed
      // join, and never again.
      const relayed = takeTeamJoinError();
      if (relayed) setJoinError(relayed);
      const data = await loadTeam();
      if (cancelled) return;
      setLoading(false);
      // The catalog is fetched in EVERY state, not just for owners: the
      // "buat tim" card quotes the plan price before a team exists, and a
      // member's plan card names what the owner is paying for. The route is
      // public, so this costs nothing and never 403s.
      loadPacks();
      if (data?.team) {
        loadLedger(null);
        loadProjects();
        if (data.role === 'owner') loadAnalytics();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scrolls a section into view inside the shell's scroll container. Used by the
  // sticky sub-nav and by the /team#topup deep link in the account menu.
  //
  // scrollIntoView({behavior:'smooth'}) measured as a NO-OP on this nested
  // container in Chrome (it moves half a pixel and stops), so the target is
  // computed against the scroller and applied directly. Smooth is still asked
  // for first; if it did not move, the position is set outright, because a
  // sub-nav that does nothing is worse than one that jumps.
  // Height of the sticky sub-nav strip. Every scroll target clears it, both in
  // the manual maths below and via scrollMarginTop on the sections themselves;
  // without it the strip sat ON TOP of the label it had just scrolled to.
  const SUBNAV_H = 56;

  // `requireOverflow` is what the scroll MATHS needs (a container that is not
  // actually scrollable would give a bogus target); the scroll LISTENER must
  // attach even before the packs and the ledger have grown the column, so it
  // asks for the nearest scrollable-by-style ancestor instead.
  const findScroller = useCallback(
    (el: HTMLElement, requireOverflow: boolean = true): HTMLElement | null => {
      let scroller: HTMLElement | null = el.parentElement;
      while (scroller) {
        const style = window.getComputedStyle(scroller);
        if (
          /(auto|scroll)/.test(style.overflowY) &&
          (!requireOverflow || scroller.scrollHeight > scroller.clientHeight)
        ) {
          return scroller;
        }
        scroller = scroller.parentElement;
      }
      return null;
    },
    []
  );

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    setActiveSection(id);
    const scroller = findScroller(el);
    if (!scroller) {
      el.scrollIntoView({ block: 'start' });
      return;
    }
    const target = Math.max(
      0,
      scroller.scrollTop +
        el.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top -
        SUBNAV_H
    );
    scroller.scrollTo({ top: target, behavior: 'smooth' });
    window.setTimeout(() => {
      if (scroller && Math.abs(scroller.scrollTop - target) > 4) {
        scroller.scrollTop = target;
      }
    }, 220);
  }, [findScroller]);

  // /team#topup (from the account menu's "Tambah kredit"). The shell has no
  // browser-native anchor scrolling, so honour the hash once the sections exist.
  useEffect(() => {
    if (loading || isCommandCenter) return;
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const target = hash === 'topup' ? 'team-topup' : `team-${hash}`;
    const timers = [
      window.setTimeout(() => scrollToSection(target), 0),
      window.setTimeout(() => scrollToSection(target), 600),
      window.setTimeout(() => scrollToSection(target), 1300),
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [loading, scrollToSection, location.hash, isCommandCenter]);

  // Keep the sub-nav honest while the user scrolls by hand: the active pill is
  // the last section whose top has passed under the strip.
  useEffect(() => {
    if (loading || !me?.team) return;
    const first = document.querySelector<HTMLElement>('[data-team-section]');
    if (!first) return;
    const scroller = findScroller(first, false);
    if (!scroller) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const sections = Array.from(
        document.querySelectorAll<HTMLElement>('[data-team-section]')
      );
      if (sections.length === 0) return;
      const scrollerTop = scroller.getBoundingClientRect().top + SUBNAV_H + 8;
      let current = sections[0].id;
      for (const section of sections) {
        if (section.getBoundingClientRect().top <= scrollerTop) current = section.id;
      }
      setActiveSection((prev) => (prev === current ? prev : current));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };
    measure();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
    // me?.role is in here because the owner-only sections (Undangan, Kredit)
    // change how many anchors exist.
  }, [loading, me?.team, me?.role, findScroller]);

  // ?topup=success / ?plan=success - the Xendit callback may still be in flight
  // when the user lands back here, so refetch once shortly after the banner.
  useEffect(() => {
    if (!paymentSuccess) return;
    const timer = setTimeout(() => {
      loadTeam();
      loadLedger(null);
    }, 3500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSuccess]);

  useEffect(() => {
    if (loading) return;
    if (isCommandCenter && me?.role !== 'owner') {
      navigate('/team', { replace: true });
    }
  }, [loading, isCommandCenter, me?.role, navigate]);

  const refresh = useCallback(async () => {
    const data = await loadTeam();
    if (data?.team) {
      loadLedger(null);
      loadProjects();
      if (data.role === 'owner') loadAnalytics();
    }
  }, [loadTeam, loadLedger, loadProjects, loadAnalytics]);

  // --- actions ------------------------------------------------------------

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    setCreateError('');
    try {
      const data = await createTeam(name);
      setMe(data);
      setNewName('');
      onDataChanged?.();
      if (data.team) {
        loadLedger(null);
        loadProjects();
        if (data.role === 'owner') loadPacks();
      }
    } catch (err) {
      const msg = describe(err);
      if (msg) setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async () => {
    const email = inviteEmail.trim();
    if (!email || inviting) return;
    setInviting(true);
    setInviteError('');
    setInviteNotice('');
    try {
      await createInvite(email);
      setInviteEmail('');
      setInviteNotice(`Undangan terkirim ke ${email}.`);
      await loadTeam();
    } catch (err) {
      const msg = describe(err);
      if (msg) setInviteError(msg);
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (inviteId: number) => {
    setInviteError('');
    setInviteNotice('');
    try {
      await revokeInvite(inviteId);
      await loadTeam();
    } catch (err) {
      const msg = describe(err);
      if (msg) setInviteError(msg);
    }
  };

  const handleResendInvites = async (ids: number[]) => {
    if (resending || ids.length === 0) return;
    setResending(true);
    setInviteError('');
    setInviteNotice('');
    try {
      for (const id of ids) await resendInvite(id);
      setInviteNotice(
        ids.length === 1
          ? 'Undangan dikirim ulang.'
          : `${ids.length} undangan dikirim ulang.`
      );
      await loadTeam();
      await loadAnalytics();
    } catch (err) {
      const msg = describe(err);
      if (msg) setInviteError(msg);
    } finally {
      setResending(false);
    }
  };

  const handleSaveLimit = async (userId: number, currentLimit: number | null) => {
    const raw = (limitDrafts[userId] ?? (currentLimit == null ? '' : String(currentLimit))).trim();
    let value: number | null = null;
    if (raw !== '') {
      const parsed = Number(raw.replace(/\./g, ''));
      if (!Number.isFinite(parsed) || parsed < 0) {
        setMemberError('Isi jumlah kredit, atau kosongkan untuk tanpa batas.');
        return;
      }
      value = Math.round(parsed);
    }
    setSavingLimitFor(userId);
    setMemberError('');
    setSavedLimitFor(null);
    try {
      await updateMemberLimit(userId, value);
      setLimitDrafts((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      await loadTeam();
      setSavedLimitFor(userId);
    } catch (err) {
      const msg = describe(err);
      if (msg) setMemberError(msg);
    } finally {
      setSavingLimitFor(null);
    }
  };

  // Confirmed inline on the row (the "Keluarkan" button becomes the question),
  // so nothing here raises a browser dialog.
  const handleRemoveMember = async (userId: number) => {
    setConfirmRemoveMember(null);
    setMemberError('');
    try {
      await removeMember(userId);
      await refresh();
      onDataChanged?.();
    } catch (err) {
      const msg = describe(err);
      if (msg) setMemberError(msg);
    }
  };

  // Any member can create a project, matching the backend contract, so there is
  // no role gate here.
  const handleCreateProject = async () => {
    const name = projectName.trim();
    if (!name || creatingProject) return;
    setCreatingProject(true);
    setProjectError('');
    try {
      const project = await createProject({ name, visibility: projectVisibility });
      setProjectName('');
      await loadProjects();
      onDataChanged?.();
      if (project?.id != null) navigate(`/p/${project.id}`);
    } catch (err) {
      if (err instanceof TeamUnauthorizedError) {
        navigate('/login');
        return;
      }
      setProjectError(
        err instanceof TeamError && err.detail ? err.detail : 'Gagal membuat project. Coba lagi.'
      );
    } finally {
      setCreatingProject(false);
    }
  };

  const handleTopup = async (packKey: string) => {
    if (topupPending) return;
    setTopupPending(packKey);
    setTopupError('');
    try {
      const result = await createTopup(packKey);
      if (result.payment_url) {
        window.location.href = result.payment_url;
        return;
      }
      setTopupError('Tautan pembayaran belum tersedia. Coba lagi sebentar.');
    } catch (err) {
      if (err instanceof TeamError && err.code === 'team_payments_closed') {
        setTopupClosed(true);
      } else {
        const msg = describe(err);
        if (msg) setTopupError(msg);
      }
    } finally {
      setTopupPending(null);
    }
  };

  // Buy or renew the monthly Team plan. Identical shape to handleTopup: the
  // backend hands back a Xendit URL and we hand the browser over to it.
  const handleBuyPlan = async () => {
    if (planPending) return;
    setPlanPending(true);
    setPlanError('');
    try {
      const result = await purchaseTeamPlan();
      if (result.payment_url) {
        window.location.href = result.payment_url;
        return;
      }
      setPlanError('Tautan pembayaran belum tersedia. Coba lagi sebentar.');
    } catch (err) {
      if (err instanceof TeamError && err.code === 'team_payments_closed') {
        setPlanClosed(true);
      } else {
        const msg = describe(err);
        if (msg) setPlanError(msg);
      }
    } finally {
      setPlanPending(false);
    }
  };

  // Buy extra seats. Same shape as handleBuyPlan: hand the browser to Xendit.
  const handleBuySeats = async () => {
    if (seatPending) return;
    setSeatPending(true);
    setSeatError('');
    try {
      const result = await purchaseTeamSeats(seatQty);
      if (result.payment_url) {
        window.location.href = result.payment_url;
        return;
      }
      setSeatError('Tautan pembayaran belum tersedia. Coba lagi sebentar.');
    } catch (err) {
      if (err instanceof TeamError && err.code === 'team_payments_closed') {
        setSeatClosed(true);
      } else {
        const msg = describe(err);
        if (msg) setSeatError(msg);
      }
    } finally {
      setSeatPending(false);
    }
  };

  const handleLeave = async () => {
    if (busyAction) return;
    setConfirmMembership(null);
    setBusyAction(true);
    setActionError('');
    try {
      await leaveTeam();
      setMe({ team: null });
      setLedger([]);
      setPacks([]);
      onDataChanged?.();
    } catch (err) {
      const msg = describe(err);
      if (msg) setActionError(msg);
    } finally {
      setBusyAction(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (busyAction) return;
    setConfirmMembership(null);
    setBusyAction(true);
    setActionError('');
    try {
      await deleteTeam();
      setMe({ team: null });
      setLedger([]);
      setPacks([]);
      onDataChanged?.();
    } catch (err) {
      const msg = describe(err);
      if (msg) setActionError(msg);
    } finally {
      setBusyAction(false);
    }
  };

  // --- shared style fragments --------------------------------------------

  // Radius scale, app-wide: 6 (micro chips) / 8 (controls, rows) / 12
  // (cards, menus) / 99 (pills). Nothing else.
  const cardStyle: React.CSSProperties = {
    background: c.panel,
    border: `1px solid ${c.line}`,
    borderRadius: '12px',
    padding: '18px',
  };

  const sectionLabelStyle: React.CSSProperties = {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: c.ink3,
    margin: '26px 2px 10px',
  };

  const inputStyle: React.CSSProperties = {
    background: c.panel2,
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '13.5px',
    color: c.ink,
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    minWidth: 0,
  };

  const primaryBtnStyle: React.CSSProperties = {
    background: c.btnPrimaryBg,
    color: c.btnPrimaryText,
    border: '1px solid transparent',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    ['--pa-btn-hover' as any]: c.btnPrimaryHover,
  };

  // A disabled primary must not read as the loudest CTA on the page. A filled
  // panel2 chip still out-weighed the ENABLED ghost buttons beside it, so it
  // drops to a transparent base at 0.45 and sits below everything actionable.
  const primaryBtn = (disabled: boolean): React.CSSProperties =>
    disabled
      ? {
          ...primaryBtnStyle,
          background: 'transparent',
          color: c.ink3,
          border: `1px solid ${c.line2}`,
          opacity: 0.45,
          cursor: 'not-allowed',
        }
      : primaryBtnStyle;

  const ghostBtnStyle: React.CSSProperties = {
    background: 'none',
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '9px 14px',
    fontSize: '12.5px',
    color: c.ink2,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  };

  // Danger variant of the ghost (the confirm half of an inline confirm pair).
  const dangerGhostBtn = (color: string): React.CSSProperties => ({
    ...ghostBtnStyle,
    color,
    borderColor: color,
    fontWeight: 600,
  });

  const monoNum: React.CSSProperties = {
    fontFamily: c.mono,
    fontVariantNumeric: 'tabular-nums',
  };

  // Sections are scroll targets for the sticky sub-nav; the offset keeps their
  // label clear of that nav instead of hiding under it. Same number the manual
  // scroll maths in scrollToSection uses, so a hash link, a pill click and a
  // native anchor jump all land on the same pixel.
  const anchorStyle: React.CSSProperties = { scrollMarginTop: `${SUBNAV_H}px` };

  const dangerColor = theme === 'light' ? '#DC2626' : '#F87171';
  const amberColor = theme === 'light' ? '#B45309' : '#F0B357';

  // Credit validity: the pool stays usable until this date and every top-up
  // pushes it 12 months out again. Absent = no purchase yet, which is NOT an
  // expiry, so the line simply does not render.
  const validUntil = me?.credits_valid_until ? parseServerDate(me.credits_valid_until) : null;
  const validityExpired = !!validUntil && !isNaN(validUntil.getTime()) && validUntil.getTime() < Date.now();

  // Team plan state. Three cases, and they are the three things the card says:
  // no plan bought yet, an active cycle (with a date), a lapsed cycle. A
  // backend that has not shipped `plan` yet reads as "belum ada plan", which is
  // the safe direction: it offers to buy rather than claiming coverage.
  const plan = me?.plan ?? null;
  const planExpires = plan?.expires_at ? parseServerDate(plan.expires_at) : null;
  const planExpiresValid = !!planExpires && !isNaN(planExpires.getTime());
  const planActive = planExpiresValid && planExpires.getTime() > Date.now();
  const planLapsed = planExpiresValid && !planActive;
  const allowanceTotal = plan?.allowance_total ?? null;
  const allowanceRemaining = plan?.allowance_remaining ?? null;

  // What the plan costs and what it gives, straight from GET /teams/packs.
  const planPriceLabel = planOffer ? formatRupiah(planOffer.price_idr) : TEAM_PLAN_PRICE_FALLBACK;
  const planOfferCredits = planOffer?.credits ?? null;
  const planOfferSeats = planOffer?.member_limit ?? null;

  // Member quota. Absent member_limit = unknown, and the quota line degrades to
  // a plain count instead of inventing a denominator. The catalog's seat count
  // is the fallback: same number, different endpoint.
  const memberLimit = me?.member_limit ?? planOfferSeats ?? null;
  const memberCount = (me?.members ?? []).length;
  const teamFull = memberLimit != null && memberCount >= memberLimit;
  const memberQuotaLabel =
    memberLimit != null
      ? `${memberCount} dari ${memberLimit} anggota`
      : `${memberCount} anggota`;

  const rolePill = (role: string) => (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '3px 8px',
        borderRadius: '99px',
        // jadeStrong, not jade: at 10px uppercase the light-theme jade fails
        // AA against the panel.
        color: role === 'owner' ? c.jadeStrong : c.ink3,
        background: role === 'owner' ? c.jadeDim : c.panel2,
        border: `1px solid ${role === 'owner' ? c.accentBorder : c.line2}`,
        whiteSpace: 'nowrap',
      }}
    >
      {role === 'owner' ? 'Pemilik' : 'Anggota'}
    </span>
  );

  // Same pill idiom as rolePill, for project visibility. Anything that is not
  // explicitly 'private' reads as a team project (the backend default).
  const visibilityPill = (visibility?: ProjectVisibility) => {
    const isTeam = visibility !== 'private';
    return (
      <span
        style={{
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          padding: '3px 8px',
          borderRadius: '99px',
          color: isTeam ? c.jadeStrong : c.ink3,
          background: isTeam ? c.jadeDim : c.panel2,
          border: `1px solid ${isTeam ? c.accentBorder : c.line2}`,
          whiteSpace: 'nowrap',
        }}
      >
        {isTeam ? 'Tim' : 'Privat'}
      </span>
    );
  };

  // --- page states --------------------------------------------------------

  const team = me?.team ?? null;
  const isOwner = me?.role === 'owner';
  const members = me?.members ?? [];
  const invites = me?.invites ?? [];

  const renderSkeleton = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <div
          key={n}
          className="pa-skeleton"
          style={{
            height: n === 1 ? '104px' : '40px',
            borderRadius: '12px',
            background: c.panel,
            animationDelay: `${n * 0.12}s`,
          }}
        />
      ))}
    </div>
  );

  const renderNoTeam = () => (
    <>
      {/* Amber, not red: a full team is not this person's mistake. The text is
          the backend's own sentence, relayed verbatim from the login screen. */}
      {joinError && (
        <div
          style={{
            ...cardStyle,
            marginBottom: '12px',
            fontSize: '12.5px',
            lineHeight: 1.7,
            color: amberColor,
            border: `1px solid ${theme === 'light' ? 'rgba(180,83,9,0.25)' : 'rgba(240,179,87,0.28)'}`,
          }}
        >
          {joinError}
        </div>
      )}
      <div style={cardStyle}>
        <div style={{ fontSize: '18px', fontWeight: 650, color: c.ink, letterSpacing: '-0.01em' }}>
          Buat tim
        </div>
        <p style={{ margin: '8px 0 0', fontSize: '13.5px', lineHeight: 1.7, color: c.ink2 }}>
          Satu plan untuk semua. Ajak keluarga atau tim kecilmu, lalu beli plan Team{' '}
          {planPriceLabel} per bulan supaya jatah bulanannya bisa dipakai bersama
          {planOfferCredits != null && planOfferSeats != null
            ? `: ${formatCredits(planOfferCredits)} kredit tiap bulan untuk maksimal ${planOfferSeats} anggota.`
            : '.'}
        </p>

        <div style={{ marginTop: '18px' }}>
          <label
            htmlFor="team-name"
            style={{ display: 'block', fontSize: '12px', color: c.ink3, marginBottom: '6px' }}
          >
            Nama tim
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', flexWrap: 'wrap' }}>
            <input
              id="team-name"
              type="text"
              value={newName}
              maxLength={MAX_TEAM_NAME}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              placeholder="Keluarga Wijaya"
              disabled={creating}
              className="pa-field"
              style={{ ...inputStyle, flex: '1 1 220px', width: 'auto' }}
            />
            <button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              className="pa-send-btn"
              style={primaryBtn(creating || !newName.trim())}
            >
              {creating ? 'Membuat…' : 'Buat tim'}
            </button>
          </div>
          <div style={{ ...monoNum, fontSize: '10.5px', color: c.ink3, marginTop: '6px' }}>
            {newName.length}/{MAX_TEAM_NAME}
          </div>
        </div>

        {createError && (
          <div style={{ marginTop: '12px', fontSize: '12.5px', color: dangerColor }}>{createError}</div>
        )}
      </div>

      <div style={{ ...cardStyle, marginTop: '12px' }}>
        <div style={{ fontSize: '12.5px', lineHeight: 1.75, color: c.ink2 }}>
          <p style={{ margin: 0 }}>
            Orang yang kamu undang lewat email tetap bisa membuat akun dan bergabung, walaupun
            pendaftaran umum Power AI sedang ditutup. Tautan undangan itulah pintunya.
          </p>
          <p style={{ margin: '10px 0 0' }}>
            Setelah bergabung, seluruh pemakaian anggota ditagih ke kredit tim. Percakapan pribadi
            tiap anggota tetap privat.
          </p>
        </div>
      </div>
    </>
  );

  const renderHeaderCard = () => (
    <div style={cardStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: '18px', fontWeight: 650, color: c.ink, letterSpacing: '-0.01em' }}>
          {team?.name}
        </div>
        {me?.role && rolePill(me.role)}
      </div>

      {/* Money moved to the plan card below; what stays here is who this team
          is: how full it is, and what THIS user has spent. */}
      <div style={{ fontSize: '12px', color: c.ink3, marginTop: '8px' }}>{memberQuotaLabel}</div>

      <div style={{ fontSize: '12.5px', color: c.ink2, marginTop: '14px' }}>
        Pemakaianmu bulan ini{' '}
        <b style={{ ...monoNum, color: c.ink, fontWeight: 600 }}>
          {formatCredits(me?.my_monthly_used ?? 0)}
        </b>{' '}
        kredit
        {me?.my_monthly_limit != null && (
          <>
            {' dari batas '}
            <b style={{ ...monoNum, color: c.ink, fontWeight: 600 }}>
              {formatCredits(me.my_monthly_limit)}
            </b>
          </>
        )}
      </div>
    </div>
  );

  // The plan card. Two wallets, side by side, because they behave differently
  // and the difference is the whole point of pricing v3: the monthly allowance
  // resets every cycle (use it or lose it), the top-up pool carries over and
  // stays valid 12 months. Owners get the buy/renew button; members see the
  // same numbers so they know what they are spending against.
  const renderPlanCard = () => {
    const statusColor = planActive ? c.jade : planLapsed ? amberColor : c.ink3;
    const statusText = planActive
      ? `Aktif sampai ${shortDate(plan?.expires_at || '')}`
      : planLapsed
        ? 'Sudah lewat, perpanjang'
        : 'Belum ada plan Team';

    return (
      <div style={{ ...cardStyle, marginTop: '12px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: 650, color: c.ink }}>Plan Team</div>
          <span
            style={{
              fontSize: '11.5px',
              fontWeight: 600,
              color: statusColor,
              whiteSpace: 'nowrap',
            }}
          >
            {statusText}
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            // 190px: two 150px tracks squeezed the 30px mono balance
            // ("3.000.000" ~ 160px) out of its track at 390px viewports.
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: '16px',
            marginTop: '16px',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span
                style={{
                  ...monoNum,
                  fontSize: '30px',
                  fontWeight: 600,
                  color: c.ink,
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                }}
              >
                {allowanceRemaining != null ? formatCredits(allowanceRemaining) : '0'}
              </span>
              {allowanceTotal != null && (
                <span style={{ ...monoNum, fontSize: '13px', color: c.ink3 }}>
                  / {formatCredits(allowanceTotal)}
                </span>
              )}
            </div>
            <div style={{ fontSize: '12px', color: c.ink3, marginTop: '4px' }}>
              Sisa jatah bulanan
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span
                style={{
                  ...monoNum,
                  fontSize: '30px',
                  fontWeight: 600,
                  color: c.ink,
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                }}
              >
                {formatCredits(me?.balance ?? 0)}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: c.ink3, marginTop: '4px' }}>Kantong top up</div>
            {validUntil && !validityExpired && (
              <div style={{ fontSize: '11.5px', color: c.ink3, marginTop: '3px' }}>
                Masa aktif sampai {shortDate(me?.credits_valid_until || '')}
              </div>
            )}
          </div>
        </div>

        <div style={{ fontSize: '11.5px', color: c.ink3, marginTop: '14px', lineHeight: 1.6 }}>
          {/* Every number in this paragraph comes from GET /teams/packs, so the
              copy follows a price or allowance change without a deploy here. */}
          {planOffer && (
            <>
              {planOffer.name} {planPriceLabel} per bulan:{' '}
              {formatCredits(planOffer.credits)} kredit jatah bulanan untuk maksimal{' '}
              {planOffer.member_limit} anggota, berlaku {planOffer.days} hari.{' '}
            </>
          )}
          Jatah bulanan diisi ulang tiap kali plan diperpanjang dan tidak menumpuk. Kredit top up
          dipakai setelah jatah bulanan habis, dan masa aktifnya setahun.
        </div>

        {validityExpired && (
          <div
            style={{
              marginTop: '10px',
              padding: '9px 11px',
              borderRadius: '8px',
              background: theme === 'light' ? 'rgba(180,83,9,0.08)' : 'rgba(240,179,87,0.10)',
              border: `1px solid ${theme === 'light' ? 'rgba(180,83,9,0.25)' : 'rgba(240,179,87,0.28)'}`,
              fontSize: '12px',
              lineHeight: 1.55,
              color: amberColor,
            }}
          >
            Masa aktif kredit top up habis. Top up untuk mengaktifkan lagi.
          </div>
        )}

        {isOwner ? (
          <>
            {planClosed ? (
              // Same calm notice the top-up uses while the money flow is frozen:
              // a statement, not an error.
              <div
                style={{
                  marginTop: '14px',
                  fontSize: '12.5px',
                  color: c.ink2,
                  lineHeight: 1.7,
                }}
              >
                Pembelian plan Team belum dibuka.
              </div>
            ) : (
              <button
                onClick={handleBuyPlan}
                disabled={planPending}
                className="pa-send-btn"
                style={{ ...primaryBtn(planPending), marginTop: '14px' }}
              >
                {planPending
                  ? 'Menyiapkan…'
                  : planActive || planLapsed
                    ? `Perpanjang ${planPriceLabel}`
                    : `Beli plan Team ${planPriceLabel}/bulan`}
              </button>
            )}
            {planError && (
              <div style={{ marginTop: '10px', fontSize: '12.5px', color: dangerColor }}>
                {planError}
              </div>
            )}

            {/* Kursi tambahan: cap saat ini + stepper + beli. Harga dari
                /teams/me (me.seat), jadi perubahan harga tidak butuh deploy. */}
            {me?.seat && (
              <div style={{
                marginTop: '16px', paddingTop: '14px',
                borderTop: `1px solid ${c.line}`,
              }}>
                <div style={{ fontSize: '12.5px', fontWeight: 600, color: c.ink }}>
                  Kursi tambahan
                </div>
                <div style={{ fontSize: '11.5px', color: c.ink3, marginTop: '3px', lineHeight: 1.6 }}>
                  Kuota sekarang {memberLimit ?? '-'} anggota
                  {(me?.extra_seats ?? 0) > 0 ? ` (termasuk ${me!.extra_seats} kursi tambahan)` : ''}.
                  Rp{me.seat.price_idr.toLocaleString('id-ID')}/orang/bulan,
                  tiap kursi menambah {formatCredits(me.seat.credits)} kredit ke jatah bulanan.
                  Makin ramai makin murah per orang.
                </div>
                {seatClosed ? (
                  <div style={{ marginTop: '10px', fontSize: '12.5px', color: c.ink2, lineHeight: 1.7 }}>
                    Pembelian kursi belum dibuka.
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: '2px',
                      border: `1px solid ${c.line2}`, borderRadius: '9px', padding: '2px',
                    }}>
                      <button
                        onClick={() => setSeatQty((q) => Math.max(1, q - 1))}
                        aria-label="Kurangi kursi"
                        className="pa-focusable pa-touch-36"
                        style={{
                          width: '26px', height: '26px', border: 'none', background: 'none',
                          cursor: 'pointer', color: c.ink2, borderRadius: '7px', fontSize: '15px',
                        }}
                      >−</button>
                      <span style={{
                        minWidth: '26px', textAlign: 'center', fontFamily: c.mono,
                        fontSize: '13px', color: c.ink,
                      }}>{seatQty}</span>
                      <button
                        onClick={() => setSeatQty((q) => Math.min(me.seat!.max_qty, q + 1))}
                        aria-label="Tambah kursi"
                        className="pa-focusable pa-touch-36"
                        style={{
                          width: '26px', height: '26px', border: 'none', background: 'none',
                          cursor: 'pointer', color: c.ink2, borderRadius: '7px', fontSize: '15px',
                        }}
                      >+</button>
                    </div>
                    <button
                      onClick={handleBuySeats}
                      disabled={seatPending}
                      className="pa-send-btn"
                      style={{ ...primaryBtn(seatPending) }}
                    >
                      {seatPending
                        ? 'Menyiapkan…'
                        : `Tambah ${seatQty} kursi · Rp${(seatQty * me.seat.price_idr).toLocaleString('id-ID')}`}
                    </button>
                  </div>
                )}
                {seatError && (
                  <div style={{ marginTop: '10px', fontSize: '12.5px', color: dangerColor }}>
                    {seatError}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div style={{ marginTop: '12px', fontSize: '11.5px', color: c.ink3, lineHeight: 1.6 }}>
            Plan Team dibeli dan diperpanjang oleh pemilik tim.
          </div>
        )}
      </div>
    );
  };

  // Adopsi AI (owner only): diagnosis + next action, bukan dashboard pemakaian.
  // Kontrak privasi: hanya angka, isi chat tidak pernah tampil.
  const renderAdoption = (mode: 'summary' | 'command') => {
    if (!isOwner) return null;
    if (analyticsStatus === 'loading' || analyticsStatus === 'idle') {
      return (
        <>
          <div style={sectionLabelStyle}>Adopsi AI</div>
          <div style={cardStyle}>
            <div style={{ fontSize: '12.5px', color: c.ink3 }}>Memuat kondisi adopsi…</div>
          </div>
        </>
      );
    }
    if (analyticsStatus === 'error' || !analytics) {
      return (
        <>
          <div style={sectionLabelStyle}>Adopsi AI</div>
          <div style={cardStyle}>
            <div style={{ fontSize: '13.5px', color: c.ink, marginBottom: '8px' }}>
              Gagal memuat adopsi.
            </div>
            <button onClick={() => loadAnalytics()} className="pa-ghost" style={ghostBtnStyle}>
              Coba lagi
            </button>
          </div>
        </>
      );
    }
    const t = analytics.totals;
    const maxWeek = Math.max(1, ...analytics.weekly.map((w) => w.credits));
    const maxMember = Math.max(1, ...analytics.members.map((m) => m.credits_month));
    const insights = (analytics.insights || []).slice(0, 3);
    const counts = analytics.state_counts || {};
    const visibleMembers = mode === 'command' && stateFilter
      ? analytics.members.filter((m) => m.adoption_state === stateFilter)
      : analytics.members;
    const runInsightAction = (action: { type: string; label: string; state?: string; invite_ids?: number[]; uuid?: string }) => {
      if (action.type === 'filter_state' && action.state) {
        navigate(`/team/adoption?state=${encodeURIComponent(action.state)}`);
        return;
      }
      if (action.type === 'resend_invites') {
        handleResendInvites(action.invite_ids || []);
        return;
      }
      if (action.type === 'scroll_topup') {
        navigate('/team#topup');
        return;
      }
      if (action.type === 'scroll_invites') {
        navigate('/team#team-undangan');
        return;
      }
      if (action.type === 'open_playbooks') {
        navigate('/team/playbooks');
        return;
      }
      if (action.type === 'open_program') {
        navigate(action.uuid ? `/team/programs/${action.uuid}` : '/team/programs');
        return;
      }
      if (action.type === 'open_help_queue') {
        navigate('/team/champion');
        return;
      }
      if (action.type === 'open_people') {
        navigate('/team/people');
        return;
      }
      navigate('/team#team-anggota');
    };
    const kpi = (value: string, label: string) => (
      <div>
        <div style={{ fontSize: '22px', fontWeight: 600, color: c.ink, fontFamily: c.mono }}>
          {value}
        </div>
        <div style={{ fontSize: '11px', color: c.ink3 }}>{label}</div>
      </div>
    );
    const chip = (label: string, n: number) => (
      <span key={label} style={{
        fontSize: '10.5px', fontFamily: c.mono, letterSpacing: '0.02em',
        color: n > 0 ? c.ink2 : c.ink3, opacity: n > 0 ? 1 : 0.6,
        border: `1px solid ${c.line}`, borderRadius: '999px', padding: '1px 7px',
        whiteSpace: 'nowrap',
      }}>{label} {n}</span>
    );
    const stateChip = (state?: string) => {
      if (!state) return null;
      const declining = state === 'declining';
      const habit = state === 'routine' || state === 'embedded';
      return (
        <span style={{
          fontSize: '10.5px',
          fontWeight: 600,
          color: habit ? c.jade : c.ink2,
          border: `1px solid ${habit ? c.jade : c.line2}`,
          background: declining ? c.panel2 : habit ? c.jadeDim : 'transparent',
          borderRadius: '999px',
          padding: '1px 7px',
          whiteSpace: 'nowrap',
        }}>
          {ADOPTION_STATE_LABEL[state] || state}
        </span>
      );
    };

    return (
      <>
        <div style={sectionLabelStyle}>Adopsi AI</div>
        <div style={{ ...cardStyle, marginBottom: '12px' }}>
          <TeamGuide
            theme={theme}
            lead="Papan skor kebiasaan tim. Isi chat tidak tampil. Yang kamu kejar: orang yang menyelesaikan pekerjaan nyata, lalu mengulanginya minggu depan."
            exampleName="3 dari 8 aktif 7 hari"
            exampleBody="Yang sudah Rutin boleh dibiarkan. Yang masih Bergabung butuh dijemput: undang masuk, atau minta mereka mulai satu Playbook, misalnya Laporan kunjungan."
            steps={[
              'Baca angka dan insight di bawah.',
              'Tekan aksi yang disarankan, misalnya buka Playbook atau Program.',
              'Cek lagi minggu depan. State anggota bergerak sendiri dari pemakaian.',
            ]}
            faqs={[
              {
                q: 'Apa yang dihitung sebagai adopsi?',
                a: 'Buka aplikasi belum cukup. Playbook yang ditandai selesai artinya hasilnya dipakai kerja sungguhan. Angka aktif 7 hari menunjukkan kebiasaan. Keduanya dibaca bersama.',
              },
            ]}
            related={[
              { name: 'Playbook', body: 'Pekerjaan yang kamu minta anggota mulai dan selesaikan.' },
              { name: 'Program adopsi', body: 'PR massal supaya yang belum mulai mendapat Misi.' },
            ]}
          />
        </div>
        <div style={cardStyle}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', alignItems: 'baseline' }}>
            {kpi(
              `${t.wau ?? t.active_members_month}/${t.joined_members ?? t.member_count}`,
              'anggota aktif 7 hari',
            )}
            {kpi(formatCredits(t.activated_7d ?? 0), 'baru aktif')}
            {kpi(
              formatCredits(t.routine_members ?? 0),
              'pemakaian berulang',
            )}
            {kpi(
              formatCredits(t.pending_invites ?? 0),
              'undangan menunggu',
            )}
          </div>

          {insights.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: c.ink, marginBottom: '8px' }}>
                Perlu perhatian
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {insights.map((insight) => (
                  <div
                    key={insight.id}
                    style={{
                      border: `1px solid ${c.line}`,
                      borderRadius: '12px',
                      padding: '10px 12px',
                    }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: 600, color: c.ink, lineHeight: 1.45 }}>
                      {insight.headline}
                    </div>
                    <div style={{ fontSize: '12px', color: c.ink2, marginTop: '4px', lineHeight: 1.55 }}>
                      {insight.why}
                    </div>
                    <div style={{ fontSize: '11px', color: c.ink3, marginTop: '4px' }}>
                      {insight.evidence}
                    </div>
                    <button
                      type="button"
                      onClick={() => runInsightAction(insight.primary_action)}
                      disabled={insight.primary_action.type === 'resend_invites' && resending}
                      className="pa-ghost"
                      style={{ ...ghostBtnStyle, marginTop: '8px' }}
                    >
                      {insight.primary_action.type === 'resend_invites' && resending
                        ? 'Mengirim ulang…'
                        : insight.primary_action.label}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(analytics.programs || []).length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: c.ink, marginBottom: '8px' }}>
                Program adopsi
              </div>
              {(analytics.programs || []).map((row) => (
                <button
                  key={row.uuid}
                  type="button"
                  onClick={() => navigate(`/team/programs/${row.uuid}`)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: `1px solid ${c.line}`,
                    borderRadius: '12px',
                    padding: '10px 12px',
                    marginBottom: '8px',
                    background: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 600, color: c.ink }}>{row.name}</div>
                  <div style={{ fontSize: '12px', color: c.ink2, marginTop: '4px' }}>
                    {row.completed} dari {row.participants} anggota selesai
                  </div>
                </button>
              ))}
            </div>
          )}

          {mode === 'summary' && (
            <button
              type="button"
              onClick={() => navigate('/team/adoption')}
              className="pa-ghost"
              style={{ ...ghostBtnStyle, marginTop: '14px' }}
            >
              Buka detail adopsi
            </button>
          )}

          {mode === 'command' && (
            <>
              <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                <button
                  type="button"
                  onClick={() => navigate('/team/adoption')}
                  className="pa-ghost"
                  style={{
                    ...ghostBtnStyle,
                    background: !stateFilter ? c.jadeDim : 'none',
                    borderColor: !stateFilter ? c.jade : c.line2,
                    fontWeight: !stateFilter ? 600 : 500,
                  }}
                >
                  Semua {analytics.members.length}
                </button>
                {ADOPTION_FILTERS.map((state) => {
                  const n = counts[state] || 0;
                  if (n === 0 && stateFilter !== state) return null;
                  const active = stateFilter === state;
                  return (
                    <button
                      key={state}
                      type="button"
                      onClick={() => navigate(`/team/adoption?state=${state}`)}
                      className="pa-ghost"
                      style={{
                        ...ghostBtnStyle,
                        background: active ? c.jadeDim : 'none',
                        borderColor: active ? c.jade : c.line2,
                        fontWeight: active ? 600 : 500,
                      }}
                    >
                      {ADOPTION_STATE_LABEL[state]} {n}
                    </button>
                  );
                })}
              </div>

              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '11px', color: c.ink3, marginBottom: '6px' }}>
                  Kredit per minggu (8 minggu terakhir)
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '46px' }}>
                  {analytics.weekly.map((w) => (
                    <div
                      key={w.week_start}
                      title={`Minggu ${shortDate(w.week_start)}: ${formatCredits(w.credits)} kredit, ${w.turns} pertanyaan`}
                      style={{
                        flex: 1,
                        height: `${Math.max(4, Math.round((w.credits / maxWeek) * 46))}px`,
                        background: w.credits > 0 ? c.jade : c.line,
                        opacity: w.credits > 0 ? 0.85 : 0.6,
                        borderRadius: '3px 3px 0 0',
                      }}
                    />
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {visibleMembers.map((m) => (
                  <div key={m.user_id} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '12.5px', color: c.ink, fontWeight: 500,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '220px',
                      }}>
                        {m.email}
                      </span>
                      {stateChip(m.adoption_state)}
                      <span style={{ fontSize: '11px', color: c.ink3, fontFamily: c.mono }}>
                        {formatCredits(m.credits_month)} kredit · {m.turns_month} tanya · {m.active_days_28d ?? m.active_days_month} hari
                      </span>
                      <span style={{ fontSize: '11px', color: c.ink3, marginLeft: 'auto' }}>
                        {m.last_active_at ? `aktif ${shortDate(m.last_active_at)}` : 'belum pernah'}
                      </span>
                    </div>
                    <div style={{ height: '4px', borderRadius: '999px', background: c.line, overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.round((m.credits_month / maxMember) * 100)}%`,
                        height: '100%', background: c.jade, opacity: 0.8,
                      }} />
                    </div>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      {chip('Riset', m.features.search)}
                      {chip('File', m.features.file)}
                      {chip('Gambar', m.features.image)}
                      {chip('Deck', m.features.presentation)}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => downloadAdoptionCsv(analytics)}
                  className="pa-ghost"
                  style={ghostBtnStyle}
                >
                  Unduh CSV
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/team')}
                  className="pa-ghost"
                  style={ghostBtnStyle}
                >
                  Kelola tim
                </button>
              </div>
            </>
          )}

          {(inviteNotice || inviteError) && (
            <div style={{
              marginTop: '10px', fontSize: '12.5px',
              color: inviteError ? c.ink : c.jade,
            }}>
              {inviteError || inviteNotice}
            </div>
          )}

          <div style={{ marginTop: '12px', fontSize: '10.5px', color: c.ink3 }}>
            {formatCredits(t.credits_month)} kredit terpakai bulan ini sebagai konteks biaya, bukan ukuran keberhasilan.
            Hanya angka agregat. Isi chat pribadi anggota tidak pernah terlihat di sini.
          </div>
        </div>
      </>
    );
  };

  const renderMembers = () => (
    <>
      <div style={sectionLabelStyle}>Anggota</div>
      <div style={{ ...cardStyle, padding: '6px 14px' }}>
        {members.length === 0 ? (
          <div style={{ padding: '14px 0', fontSize: '12.5px', color: c.ink3 }}>
            Belum ada anggota.
          </div>
        ) : (
          members.map((m, idx) => (
            // Fixed grid, not a wrapping flex row: the mono credit figure has to
            // land on ONE x across every row, which "flex: 1 1 200px" could
            // never guarantee. Below 640px it stacks instead of squeezing.
            <div
              key={m.user_id}
              className={isOwner ? 'pa-member-row' : 'pa-member-row pa-member-row--plain'}
              style={{
                padding: '13px 0',
                borderTop: idx === 0 ? 'none' : `1px solid ${c.line}`,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '13px',
                    color: c.ink,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {m.email}
                </div>
                <div style={{ marginTop: '5px' }}>{rolePill(m.role)}</div>
              </div>

              <div className="pa-member-credits" style={{ minWidth: 0 }}>
                <div style={{ ...monoNum, fontSize: '13px', color: c.ink, fontWeight: 600 }}>
                  {formatCredits(m.used_this_month)}
                </div>
                <div style={{ fontSize: '10.5px', color: c.ink3 }}>kredit bulan ini</div>
              </div>

              {isOwner && (
                <div className="pa-member-actions" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0 }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label={`Batas bulanan untuk ${m.email}`}
                    value={
                      limitDrafts[m.user_id] ??
                      (m.monthly_limit == null ? '' : String(m.monthly_limit))
                    }
                    onChange={(e) => {
                      // Typing again means the confirmation no longer describes
                      // what is in the field.
                      if (savedLimitFor === m.user_id) setSavedLimitFor(null);
                      setLimitDrafts((prev) => ({ ...prev, [m.user_id]: e.target.value }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveLimit(m.user_id, m.monthly_limit);
                    }}
                    placeholder="Tanpa batas"
                    className="pa-field"
                    style={{ ...inputStyle, width: '108px', padding: '8px 9px', fontSize: '12.5px' }}
                  />
                  <button
                    onClick={() => handleSaveLimit(m.user_id, m.monthly_limit)}
                    disabled={savingLimitFor === m.user_id}
                    className="pa-ghost"
                    style={{
                      ...ghostBtnStyle,
                      opacity: savingLimitFor === m.user_id ? 0.45 : 1,
                    }}
                  >
                    {savingLimitFor === m.user_id ? 'Menyimpan…' : 'Simpan'}
                  </button>
                  {savedLimitFor === m.user_id && savingLimitFor !== m.user_id && (
                    <span style={{ fontSize: '12.5px', color: c.jade, whiteSpace: 'nowrap' }}>
                      Tersimpan.
                    </span>
                  )}
                  {m.role !== 'owner' && (
                    confirmRemoveMember === m.user_id ? (
                      <>
                        <button
                          onClick={() => handleRemoveMember(m.user_id)}
                          className="pa-ghost"
                          autoFocus
                          title={`Keluarkan ${m.email} dari tim. Pemakaian yang sudah tercatat tetap tersimpan.`}
                          style={dangerGhostBtn(dangerColor)}
                        >
                          Yakin keluarkan?
                        </button>
                        <button
                          onClick={() => setConfirmRemoveMember(null)}
                          className="pa-ghost"
                          style={ghostBtnStyle}
                        >
                          Batal
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setConfirmRemoveMember(m.user_id)}
                        title="Keluarkan dari tim"
                        className="pa-ghost"
                        style={{ ...ghostBtnStyle, color: dangerColor, borderColor: c.line2 }}
                      >
                        Keluarkan
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          ))
        )}
        {memberError && (
          <div style={{ padding: '0 0 12px', fontSize: '12.5px', color: dangerColor }}>
            {memberError}
          </div>
        )}
      </div>
      {isOwner && (
        <div style={{ fontSize: '11.5px', color: c.ink3, margin: '8px 2px 0', lineHeight: 1.6 }}>
          Batas bulanan dihitung per bulan kalender. Kosongkan kolomnya kalau anggota itu tidak
          perlu dibatasi.
        </div>
      )}
      <div style={{ marginTop: '10px' }}>
        <button
          type="button"
          onClick={() => navigate('/team/people')}
          className="pa-ghost"
          style={ghostBtnStyle}
        >
          Kelola grup
        </button>
      </div>
    </>
  );

  const renderInvites = () => (
    <>
      <div style={sectionLabelStyle}>Undangan</div>
      <div style={cardStyle}>
        {/* A full team cannot accept anyone, so the form says so BEFORE the
            owner types an email and gets a 409 back from the server. */}
        {teamFull && (
          <div style={{ marginBottom: '12px', fontSize: '12.5px', color: c.ink2, lineHeight: 1.7 }}>
            Tim sudah penuh, {memberQuotaLabel}. Keluarkan satu anggota dulu kalau mau mengundang
            orang baru.
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', flexWrap: 'wrap' }}>
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleInvite();
            }}
            placeholder="nama@email.com"
            aria-label="Email yang diundang"
            disabled={inviting || teamFull}
            className="pa-field"
            style={{ ...inputStyle, flex: '1 1 220px', width: 'auto', opacity: teamFull ? 0.55 : 1 }}
          />
          <button
            onClick={handleInvite}
            disabled={inviting || teamFull || !inviteEmail.trim()}
            className="pa-send-btn"
            style={primaryBtn(inviting || teamFull || !inviteEmail.trim())}
          >
            {inviting ? 'Mengirim…' : 'Kirim undangan'}
          </button>
        </div>

        {inviteNotice && (
          <div style={{ marginTop: '10px', fontSize: '12.5px', color: c.jade }}>{inviteNotice}</div>
        )}
        {inviteError && (
          <div style={{ marginTop: '10px', fontSize: '12.5px', color: dangerColor }}>
            {inviteError}
          </div>
        )}

        <div style={{ marginTop: '16px', borderTop: `1px solid ${c.line}`, paddingTop: '4px' }}>
          {invites.length === 0 ? (
            <div style={{ padding: '12px 0 2px', fontSize: '12.5px', color: c.ink3 }}>
              Belum ada undangan yang menunggu.
            </div>
          ) : (
            invites.map((inv, idx) => (
              <div
                key={inv.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                  padding: '12px 0',
                  borderTop: idx === 0 ? 'none' : `1px solid ${c.line}`,
                }}
              >
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '13px',
                      color: c.ink,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {inv.email}
                  </div>
                  <div style={{ fontSize: '11px', color: c.ink3, marginTop: '3px' }}>
                    Berlaku sampai {shortDate(inv.expires_at)}
                  </div>
                </div>
                <button onClick={() => handleRevoke(inv.id)} className="pa-ghost" style={ghostBtnStyle}>
                  Batalkan
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );

  const renderProjects = () => (
    <>
      <div style={sectionLabelStyle}>Project</div>
      <div style={{ ...cardStyle, marginBottom: '12px' }}>
        <TeamGuide
          theme={theme}
          lead="Folder chat dan file untuk satu tujuan. Instruksi Project ikut ke setiap chat di dalamnya. Playbook bisa tertaut ke Project, tapi Playbook tetap SOP-nya."
          exampleName="Q3 Retail"
          exampleBody="File harga dan brief klien ada di sini, jadi chat baru tidak mulai dari nol. Sari yang menjalankan Playbook Laporan kunjungan di Project ini tetap memakai file yang sama."
          steps={[
            'Buat Project, isi nama dan instruksi singkat.',
            'Mulai chat baru dari Project, atau pindahkan chat yang sudah ada.',
            'Upload file yang dipakai bersama. Anggota tim melihat Project yang dibagikan ke tim.',
          ]}
        />
      </div>
      <div style={{ ...cardStyle, padding: '6px 14px 14px' }}>
        {projects.length === 0 ? (
          <div style={{ padding: '14px 0', fontSize: '12.5px', color: c.ink3, lineHeight: 1.7 }}>
            Belum ada Project. Buat yang pertama di bawah.
          </div>
        ) : (
          // The whole row is one real <button>: keyboard reachable, and a 44px
          // tap target instead of the 19.5px title text it used to be.
          projects.map((p, idx) => (
            <React.Fragment key={p.id}>
              {idx > 0 && <div style={{ height: '1px', background: c.line }} />}
              <button
                type="button"
                onClick={() => navigate(`/p/${p.id}`)}
                // Name first, always: the description alone was a tooltip that
                // never said WHICH project it belonged to.
                title={p.description ? `${p.name} · ${p.description}` : p.name}
                className="pa-row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                  width: 'calc(100% + 16px)',
                  marginLeft: '-8px',
                  marginRight: '-8px',
                  padding: '12px 8px',
                  minHeight: '44px',
                  background: 'none',
                  border: 'none',
                  borderRadius: '8px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  color: 'inherit',
                }}
              >
                <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: c.ink,
                        maxWidth: '100%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {p.name}
                    </span>
                    {visibilityPill(p.visibility)}
                  </span>
                  {p.description && (
                    <span
                      style={{
                        display: 'block',
                        fontSize: '11.5px',
                        color: c.ink3,
                        marginTop: '4px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {p.description}
                    </span>
                  )}
                </span>

                <span
                  style={{
                    ...monoNum,
                    fontSize: '12px',
                    color: c.ink3,
                    whiteSpace: 'nowrap',
                    textAlign: 'right',
                  }}
                >
                  {p.conversation_count ?? 0} chat
                </span>
              </button>
            </React.Fragment>
          ))
        )}

        <div style={{ borderTop: `1px solid ${c.line}`, paddingTop: '14px', marginTop: '2px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={projectName}
              maxLength={MAX_PROJECT_NAME}
              onChange={(e) => setProjectName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateProject();
              }}
              placeholder="Nama project"
              aria-label="Nama project"
              disabled={creatingProject}
              className="pa-field"
              style={{ ...inputStyle, flex: '1 1 200px', width: 'auto' }}
            />
            {/* appearance:none + our own chevron: the native select control was
                the one macOS-chrome widget left in the panel and it broke the
                Obsidian field idiom beside it. */}
            <div style={{ position: 'relative', flex: '0 1 130px', minWidth: '116px' }}>
              <select
                value={projectVisibility}
                onChange={(e) =>
                  setProjectVisibility(e.target.value === 'private' ? 'private' : 'team')
                }
                aria-label="Siapa yang bisa melihat project ini"
                disabled={creatingProject}
                className="pa-field"
                style={{
                  ...inputStyle,
                  width: '100%',
                  cursor: 'pointer',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  paddingRight: '30px',
                }}
              >
                <option value="team">Tim</option>
                <option value="private">Privat</option>
              </select>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke={c.ink3}
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  right: '11px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                }}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </div>
            <button
              onClick={handleCreateProject}
              disabled={creatingProject || !projectName.trim()}
              className="pa-send-btn"
              style={primaryBtn(creatingProject || !projectName.trim())}
            >
              {creatingProject ? 'Membuat…' : 'Buat project'}
            </button>
          </div>

          <div style={{ fontSize: '11.5px', color: c.ink3, marginTop: '8px', lineHeight: 1.6 }}>
            Project tim terlihat oleh semua anggota. Chat yang dimasukkan ke project tim
            otomatis dibagikan.
          </div>

          {projectError && (
            <div style={{ marginTop: '10px', fontSize: '12.5px', color: dangerColor }}>
              {projectError}
            </div>
          )}
        </div>
      </div>
    </>
  );

  const renderTopup = () => (
    <>
      <div style={sectionLabelStyle}>Top up</div>
      <div style={{ fontSize: '11.5px', color: c.ink3, margin: '-4px 2px 10px', lineHeight: 1.6 }}>
        Tambahan kredit untuk dipakai setelah jatah bulanan habis. Masa aktifnya 12 bulan dan
        sisanya tidak hangus saat bulan berganti.
      </div>
      {topupClosed && (
        <div
          style={{
            ...cardStyle,
            marginBottom: '12px',
            fontSize: '12.5px',
            color: c.ink2,
            lineHeight: 1.7,
          }}
        >
          Pembelian kredit top up belum dibuka.
        </div>
      )}
      {topupError && (
        <div style={{ margin: '0 2px 10px', fontSize: '12.5px', color: dangerColor }}>
          {topupError}
        </div>
      )}
      {packs.length === 0 ? (
        <div style={{ ...cardStyle, fontSize: '12.5px', color: c.ink3 }}>
          Paket top up belum tersedia.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: '12px',
            opacity: topupClosed ? 0.55 : 1,
          }}
        >
          {/* The live catalog sends no badges, so the reserved badge row would
              be 24px of dead space in every card. It comes back the moment any
              pack carries one, and then it is reserved in ALL of them so the
              buttons keep landing on one baseline. */}
          {packs.map((pack) => {
            const highlighted = !!pack.badge;
            const anyBadge = packs.some((p) => !!p.badge);
            return (
              <div
                key={pack.key}
                style={{
                  ...cardStyle,
                  border: `1px solid ${highlighted ? c.jade : c.line}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {/* Fixed-height badge row: reserved in every card (badge or
                    not) so the "Beli paket" buttons land on one baseline. */}
                {anyBadge && (
                <div style={{ height: '24px', display: 'flex', alignItems: 'center' }}>
                  {pack.badge && (
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        padding: '3px 8px',
                        borderRadius: '99px',
                        color: c.jade,
                        background: c.jadeDim,
                        border: `1px solid ${c.accentBorder}`,
                      }}
                    >
                      {pack.badge}
                    </span>
                  )}
                </div>
                )}
                <div style={{ fontSize: '14px', fontWeight: 600, color: c.ink }}>{pack.name}</div>
                <div style={{ ...monoNum, fontSize: '20px', fontWeight: 600, color: c.ink }}>
                  {formatRupiah(pack.price_idr)}
                </div>
                <div style={{ fontSize: '12px', color: c.ink2 }}>
                  <span style={{ ...monoNum, color: c.ink }}>{formatCredits(pack.credits)}</span> kredit
                </div>
                <button
                  onClick={() => handleTopup(pack.key)}
                  disabled={topupClosed || topupPending === pack.key}
                  className="pa-send-btn"
                  style={{
                    ...primaryBtn(topupClosed || topupPending === pack.key),
                    marginTop: 'auto',
                    width: '100%',
                  }}
                >
                  {topupPending === pack.key ? 'Menyiapkan…' : 'Beli top up'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  const renderLedger = () => {
    const cellBase: React.CSSProperties = {
      padding: '9px 10px',
      borderTop: `1px solid ${c.line}`,
      verticalAlign: 'top',
    };
    const headCell: React.CSSProperties = {
      padding: '0 10px 8px',
      textAlign: 'left',
      fontSize: '10px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: c.ink3,
      whiteSpace: 'nowrap',
    };
    return (
      <>
        <div style={sectionLabelStyle}>Riwayat kredit</div>
        <div style={cardStyle}>
          {ledger.length === 0 && !ledgerLoading ? (
            <div style={{ fontSize: '12.5px', color: c.ink3 }}>Belum ada riwayat kredit.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '12.5px',
                  minWidth: '460px',
                }}
              >
                <thead>
                  <tr>
                    <th style={headCell}>Tanggal</th>
                    <th style={headCell}>Keterangan</th>
                    <th style={{ ...headCell, textAlign: 'right' }}>Jumlah</th>
                    <th style={headCell}>Anggota</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((entry) => (
                    <tr key={entry.id}>
                      <td style={{ ...cellBase, color: c.ink3, whiteSpace: 'nowrap' }}>
                        {shortDate(entry.created_at)}
                      </td>
                      <td style={{ ...cellBase, color: c.ink2 }}>{entry.description}</td>
                      <td
                        style={{
                          ...cellBase,
                          ...monoNum,
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          fontWeight: 600,
                          color: entry.amount > 0 ? c.jade : c.ink,
                        }}
                      >
                        {entry.amount > 0 ? '+' : entry.amount < 0 ? '-' : ''}
                        {formatCredits(Math.abs(entry.amount))}
                      </td>
                      <td style={{ ...cellBase, color: c.ink3 }}>{entry.user_email || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {ledgerError && (
            <div style={{ marginTop: '10px', fontSize: '12.5px', color: dangerColor }}>
              {ledgerError}
            </div>
          )}

          {ledgerHasMore && (
            <div style={{ marginTop: '12px' }}>
              <button
                onClick={() => loadLedger(ledger.length ? ledger[ledger.length - 1].id : null)}
                disabled={ledgerLoading}
                className="pa-ghost"
                style={{ ...ghostBtnStyle, opacity: ledgerLoading ? 0.45 : 1 }}
              >
                {ledgerLoading ? 'Memuat…' : 'Muat lagi'}
              </button>
            </div>
          )}
        </div>
      </>
    );
  };

  // Sticky in-panel section nav. The panel is long (7 sections) and lives in a
  // scroll container that starts under the shell's top bar, so this is the only
  // way to reach "Riwayat" without a scroll marathon.
  const renderSectionNav = () => {
    const items: { id: string; label: string }[] = [
      { id: 'team-anggota', label: 'Anggota' },
      { id: 'team-people', label: 'Grup' },
      { id: 'team-playbooks', label: 'Playbook' },
      { id: 'team-programs', label: 'Program adopsi' },
      { id: 'team-champion', label: 'Champion AI' },
      ...(isOwner ? [{ id: 'team-adopsi', label: 'Adopsi' }] : []),
      ...(isOwner ? [{ id: 'team-undangan', label: 'Undangan' }] : []),
      { id: 'team-project', label: 'Project' },
      ...(isOwner ? [{ id: 'team-topup', label: 'Top up' }] : []),
      { id: 'team-riwayat', label: 'Riwayat' },
    ];
    return (
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 4,
          display: 'flex',
          gap: '6px',
          flexWrap: 'wrap',
          padding: '8px 0 10px',
          marginBottom: '6px',
          background: theme === 'dark' ? 'rgba(10,11,13,0.86)' : 'rgba(247,248,250,0.9)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${c.line}`,
        }}
      >
        {items.map((item) => {
          // The strip used to render five identical pills with no "you are
          // here" at all. The active one is tinted jade and carries
          // aria-current, so it reads for a screen reader too.
          const active = activeSection === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                if (item.id === 'team-adopsi') navigate('/team/adoption');
                else if (item.id === 'team-people') navigate('/team/people');
                else if (item.id === 'team-playbooks') navigate('/team/playbooks');
                else if (item.id === 'team-programs') navigate('/team/programs');
                else if (item.id === 'team-champion') navigate('/team/champion');
                else scrollToSection(item.id);
              }}
              aria-current={active ? 'true' : undefined}
              className="pa-ghost"
              style={{
                background: active ? c.jadeDim : 'none',
                border: `1px solid ${active ? c.jade : c.line2}`,
                borderRadius: '99px',
                padding: '5px 12px',
                fontSize: '11.5px',
                fontWeight: active ? 600 : 500,
                color: active ? c.ink : c.ink2,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    );
  };

  // Plain labelled rows, not a "danger zone": the label now says what the row
  // does ("Keluar dari tim" / "Hapus tim") instead of shouting a warning.
  const renderDangerZone = () => {
    const canDelete = isOwner && members.length <= 1;
    return (
      <>
        <div style={sectionLabelStyle}>Keanggotaan</div>
        <div style={cardStyle}>
          {!isOwner ? (
            <>
              {confirmMembership === 'leave' && !busyAction ? (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    onClick={handleLeave}
                    className="pa-ghost"
                    autoFocus
                    style={dangerGhostBtn(dangerColor)}
                  >
                    Yakin keluar?
                  </button>
                  <button
                    onClick={() => setConfirmMembership(null)}
                    className="pa-ghost"
                    style={ghostBtnStyle}
                  >
                    Batal
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmMembership('leave')}
                  disabled={busyAction}
                  className="pa-ghost"
                  style={{ ...ghostBtnStyle, color: dangerColor, opacity: busyAction ? 0.45 : 1 }}
                >
                  Keluar dari tim
                </button>
              )}
              <div style={{ marginTop: '8px', fontSize: '11.5px', color: c.ink3, lineHeight: 1.6 }}>
                Setelah keluar, pemakaianmu tidak lagi ditagih ke kredit tim.
              </div>
            </>
          ) : canDelete ? (
            <>
              {confirmMembership === 'delete' && !busyAction ? (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    onClick={handleDeleteTeam}
                    className="pa-ghost"
                    autoFocus
                    title="Sisa kredit di kantong tim ikut hilang dan tidak bisa dikembalikan."
                    style={dangerGhostBtn(dangerColor)}
                  >
                    Yakin hapus tim?
                  </button>
                  <button
                    onClick={() => setConfirmMembership(null)}
                    className="pa-ghost"
                    style={ghostBtnStyle}
                  >
                    Batal
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmMembership('delete')}
                  disabled={busyAction}
                  className="pa-ghost"
                  style={{ ...ghostBtnStyle, color: dangerColor, opacity: busyAction ? 0.45 : 1 }}
                >
                  Hapus tim
                </button>
              )}
              <div style={{ marginTop: '8px', fontSize: '11.5px', color: c.ink3, lineHeight: 1.6 }}>
                Tim bisa dihapus selama kamu masih satu-satunya anggota. Sisa kredit di kantong tim
                ikut hilang.
              </div>
            </>
          ) : (
            <div style={{ fontSize: '11.5px', color: c.ink3, lineHeight: 1.6 }}>
              Sebagai pemilik, kamu tidak bisa keluar dari tim. Tim baru bisa dihapus setelah semua
              anggota lain dikeluarkan.
            </div>
          )}
          {actionError && (
            <div style={{ marginTop: '10px', fontSize: '12.5px', color: dangerColor }}>
              {actionError}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    // No chrome of its own: the shell owns the scroll container, the top bar and
    // the way back. This is just the 820px card column.
    <div
      style={{
        maxWidth: '820px',
        margin: '0 auto',
        padding: '18px 18px 72px',
        color: c.ink,
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        // Interaction-state vars consumed by .pa-field / .pa-ghost / .pa-row
        // in index.css (same inline-var idiom as the composer's --pa-btn-hover).
        ['--pa-focus-ring' as any]: c.jade,
        ['--pa-focus-ring-dim' as any]: c.jadeDim,
        ['--pa-ghost-hover-bg' as any]: c.panel2,
        ['--pa-ghost-hover-line' as any]: c.line2,
        ['--pa-row-hover-bg' as any]: c.panel,
      }}
    >
      {team && !loading && !isCommandCenter && !isPeopleSurface && !isPlaybooksSurface && !isProgramsSurface && !isChampionSurface && renderSectionNav()}
      <div>
        {paymentSuccess && (
          <div
            style={{
              ...cardStyle,
              border: `1px solid ${c.accentBorder}`,
              background: c.jadeDim,
              marginBottom: '14px',
              fontSize: '12.5px',
              color: c.ink,
              lineHeight: 1.7,
            }}
          >
            {paymentSuccess === 'plan'
              ? 'Pembayaran sudah diproses. Jatah bulanan tim akan aktif begitu pembayaranmu dikonfirmasi.'
              : paymentSuccess === 'seats'
                ? 'Pembayaran sudah diproses. Kuota anggota tim akan bertambah begitu pembayaranmu dikonfirmasi.'
                : 'Pembayaran sudah diproses. Kredit tim akan bertambah begitu pembayaranmu dikonfirmasi.'}
            <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => refresh()} className="pa-ghost" style={ghostBtnStyle}>
                Segarkan
              </button>
              <button onClick={() => setPaymentSuccess(null)} className="pa-ghost" style={ghostBtnStyle}>
                Tutup
              </button>
            </div>
          </div>
        )}

        {loading ? (
          renderSkeleton()
        ) : loadError && !team ? (
          <div style={cardStyle}>
            <div style={{ fontSize: '13.5px', color: c.ink, marginBottom: '10px' }}>
              Gagal memuat data tim.
            </div>
            <div style={{ fontSize: '12.5px', color: c.ink2, marginBottom: '14px' }}>{loadError}</div>
            <button onClick={() => refresh()} className="pa-ghost" style={ghostBtnStyle}>
              Coba lagi
            </button>
          </div>
        ) : !team ? (
          renderNoTeam()
        ) : isCommandCenter ? (
          <>
            {renderHeaderCard()}
            {isOwner && (
              <div id="team-adopsi" data-team-section style={anchorStyle}>
                {renderAdoption('command')}
              </div>
            )}
          </>
        ) : isPeopleSurface && me ? (
          <>
            {renderHeaderCard()}
            <TeamPeople theme={theme} me={me} />
          </>
        ) : isPlaybooksSurface && me ? (
          <>
            {renderHeaderCard()}
            <TeamPlaybooks theme={theme} me={me} />
          </>
        ) : isProgramsSurface && me ? (
          <>
            {renderHeaderCard()}
            <TeamPrograms theme={theme} me={me} />
          </>
        ) : isChampionSurface && me ? (
          <>
            {renderHeaderCard()}
            <TeamChampion theme={theme} me={me} />
          </>
        ) : (
          <>
            {renderHeaderCard()}
            {renderPlanCard()}
            <div id="team-anggota" data-team-section style={anchorStyle}>{renderMembers()}</div>
            {isOwner && (
              <div id="team-adopsi" data-team-section style={anchorStyle}>{renderAdoption('summary')}</div>
            )}
            {isOwner && <div id="team-undangan" data-team-section style={anchorStyle}>{renderInvites()}</div>}
            <div id="team-project" data-team-section style={anchorStyle}>{renderProjects()}</div>
            {isOwner && <div id="team-topup" data-team-section style={anchorStyle}>{renderTopup()}</div>}
            <div id="team-riwayat" data-team-section style={anchorStyle}>{renderLedger()}</div>
            {renderDangerZone()}
          </>
        )}
      </div>
    </div>
  );
};

export default TeamPanel;
export { TeamPanel };
